<template>
  <div class="tw-inline-block">
    <BaseButton
      v-if="isAvailable"
      :color="buttonColor"
      :size="buttonSize"
      is-block
      @click="handleButtonClick"
      @mousedown.middle="handleButtonClick({ middleClick: true })"
    >
      <slot>Request a Call</slot>
    </BaseButton>
    <BaseButton v-else color="primary" :size="buttonSize" is-block is-disabled>
      Currently Unavailable
    </BaseButton>

    <FreePaybackSessionsLimitModal
      v-if="accessControl.subscription.privileges.freePaybackCalls.limitHit"
      :name="freePaybackSessionsLimitModalName"
    />
    <FreeSessionsLimitModal
      v-if="accessControl.subscription.privileges.freeCalls.limitHit"
      :name="freeSessionsLimitModalName"
    />
    <FreeMonthlySessionsLimitModal
      v-if="accessControl.subscription.privileges.freeCallsPerMonth.limitHit"
      :name="freeMonthlySessionsLimitModalName"
    />
    <TrialExpiredModal
      v-if="isSubscriptionTier(SUBSCRIPTION_TIER.TRIAL)"
      :name="trialExpiredModalName"
    />
  </div>
</template>

<script setup>
import BaseButton from './BaseButton.vue'
import FreeSessionsLimitModal from './modals/FreeSessionsLimitModal.vue'
import { getUniqueId } from '@/shared/utils/uniqueId.js'
import TrialExpiredModal from '@/shared/components/modals/TrialExpiredModal.vue'
import {
  ACCESS_TYPE,
  ROLE,
  SUBSCRIPTION_TIER,
} from '@/shared/data/permissionSystem.js'
import FreeMonthlySessionsLimitModal from '@/shared/components/modals/FreeMonthlySessionsLimitModal.vue'
import FreePaybackSessionsLimitModal from '@/shared/components/modals/FreePaybackSessionsLimitModal.vue'
import { router } from '@inertiajs/vue3'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { useModals } from '@/shared/composables/useModals.js'
import { useCurrentUser } from '@/shared/composables/useCurrentUser.js'

const props = defineProps({
  isAvailable: {
    type: Boolean,
    required: true,
  },
  isMentorChargeable: {
    type: Boolean,
    required: true,
  },
  mentorSlug: {
    type: String,
    required: true,
  },
  buttonColor: {
    type: String,
    default: 'primary',
  },
  buttonSize: {
    type: String,
    default: 'regular',
  },
})

const freePaybackSessionsLimitModalName = `growthmentor-free-payback-sessions-limit-${getUniqueId()}`
const freeSessionsLimitModalName = `growthmentor-free-sessions-limit-${getUniqueId()}`
const freeMonthlySessionsLimitModalName = `growthmentor-free-monthly-sessions-limit-${getUniqueId()}`
const trialExpiredModalName = `growthmentor-trial-expired-${getUniqueId()}`

const { openModal } = useModals()
const { hasRole, hasAccess, isSubscriptionTier, accessControl } =
  usePermissionSystem()
const { currentUser } = useCurrentUser()

const handleButtonClick = ({ middleClick }) => {
  const openPage = (url) => {
    middleClick ? window.open(url, '_blank') : router.visit(url)
  }

  if (hasRole(ROLE.GUEST)) {
    return openPage(`/mentors/${props.mentorSlug}/sessions/new`)
  }
  if (hasAccess(ACCESS_TYPE.NONE)) {
    return openPage(`/mentors/${props.mentorSlug}/sessions/new`)
  }
  if (
    accessControl.value.subscription.privileges.freeCalls.limitHit &&
    isSubscriptionTier(SUBSCRIPTION_TIER.TRIAL)
  ) {
    return openModal(trialExpiredModalName)
  }
  if (!props.isMentorChargeable) {
    const {
      slug,
      email,
      name,
      firstName,
      lastName,
      role,
      subscriptionPlanName,
    } = currentUser.value

    // Payback limit
    if (accessControl.value.subscription.privileges.freePaybackCalls.limitHit) {
      window.segmentAnalytics.track('Tit for Tat Limit Hit', {
        user_id: slug,
        user: {
          email,
          name,
          first_name: firstName,
          last_name: lastName,
          role,
        },
      })
      return openModal(freePaybackSessionsLimitModalName)
    }

    // Monthly limit
    if (
      accessControl.value.subscription.privileges.freeCallsPerMonth.limitHit
    ) {
      window.segmentAnalytics.track('Monthly Session Limit Hit', {
        user_id: slug,
        user: {
          email,
          name,
          first_name: firstName,
          last_name: lastName,
          role,
        },
      })
      return openModal(freeMonthlySessionsLimitModalName)
    }

    // Queue limit
    if (accessControl.value.subscription.privileges.freeCalls.limitHit) {
      window.segmentAnalytics.track('Session Limit Hit', {
        user_id: slug,
        user: {
          email,
          name,
          first_name: firstName,
          last_name: lastName,
          role,
          membership_plan: subscriptionPlanName,
        },
      })
      return openModal(freeSessionsLimitModalName)
    }
  }

  return openPage(`/mentors/${props.mentorSlug}/sessions/new`)
}
</script>
